import { Chip, ChipSelect } from '@/dsm-components';
import { useGetUtilitiesQuery } from '@/services/utilities';
import { PalmettoIcon } from '@palmetto/dsm-components';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useWatch, Control, UseFormSetValue, UseFormGetValues } from 'react-hook-form';

import {
  BatteryType,
  BatteryTypeLabels,
  ChecklistConditionName,
  ChecklistConditionNameLabels,
  ChecklistConditionOperator,
  OPERATORLESS_CONDITIONS,
  ParameterizedConditions,
  FinanceTypeLabels,
  ProgramTypeLabels,
  VendorEquipmentTypes,
} from 'types';

import { AVLManufacturerParamAdder } from './AVLManufacturerParamAdder';
import { ChecklistConditionOperatorLabels, DialogFormValues } from './checklistItemInputOptions';
import { DesignPlatformParamAdder } from './DesignPlatformParamAdder';
import { OrganizationParamAdder } from './OrganizationParamAdder';
import { parseParamsRaw, serializeParamsRaw } from './paramsRaw';
import { UtilityParamAdder } from './UtilityParamAdder';

/* ─── Constants ─── */

const batteryTypeOptions = [
  { value: 'any', label: 'Any' },
  { value: BatteryType.arbitrageBattery, label: BatteryTypeLabels[BatteryType.arbitrageBattery] },
  { value: BatteryType.backupBattery, label: BatteryTypeLabels[BatteryType.backupBattery] },
];

const conditionEquipmentType: Partial<Record<ChecklistConditionName, VendorEquipmentTypes>> = {
  [ChecklistConditionName.panelManufacturer]: VendorEquipmentTypes.panel,
  [ChecklistConditionName.inverterManufacturer]: VendorEquipmentTypes.inverter,
  [ChecklistConditionName.batteryManufacturer]: VendorEquipmentTypes.storage,
  [ChecklistConditionName.mountingManufacturer]: VendorEquipmentTypes.mounting,
};

// Operator select options, in enum declaration order — shared source of truth for labels.
const operatorOptions = Object.entries(ChecklistConditionOperatorLabels).map(([value, label]) => ({ value, label }));

/* ─── Removable param tag ─── */

interface ParamTagProps {
  param: string;
  displayLabel: string;
  onRemove: (param: string) => void;
  className?: string;
}

const ParamTag = ({ param, displayLabel, onRemove, className }: ParamTagProps) => {
  const handleClick = useCallback(() => {
    onRemove(param);
  }, [onRemove, param]);

  return (
    <Chip variant="lumen" className={className}>
      {displayLabel}
      <button
        type="button"
        onClick={handleClick}
        className="ml-6 cursor-pointer border-none bg-transparent p-0 text-text-tertiary hover:text-text-primary"
        aria-label={`Remove ${displayLabel}`}
      >
        <PalmettoIcon name="x" size="xs" />
      </button>
    </Chip>
  );
};

/* ─── Inline text param adder (state codes, LSE IDs, org IDs) ─── */

interface TextParamAdderProps {
  fieldPrefix: `conditions` | `inputs.${number}.conditions`;
  currentParams: string[];
  conditionIndex: number;
  setValue: UseFormSetValue<DialogFormValues>;
  placeholder?: string;
  validate?: (value: string) => string | null;
  transform?: (value: string) => string;
}

const TextParamAdder = ({
  fieldPrefix,
  currentParams,
  conditionIndex,
  setValue,
  placeholder = '+ add',
  validate,
  transform,
}: TextParamAdderProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [inputWidth, setInputWidth] = useState(60);

  const inputStyle = useMemo(
    () => ({
      width: `${inputWidth}px`,
    }),
    [inputWidth],
  );

  const resizeInput = useCallback(() => {
    if (!inputRef.current) return;
    const val = inputRef.current.value || inputRef.current.placeholder;
    const charWidth = val.length * 8 + 44;
    setInputWidth(Math.max(60, Math.min(charWidth, 200)));
  }, []);

  const [error, setError] = useState('');

  // Shared add/dedupe/validate path for both the single-value (type + Enter/blur) and
  // multi-value (newline-delimited paste) commit flows. Values are committed verbatim —
  // comma is a literal character here, never a delimiter.
  const commitValues = useCallback(
    (rawValues: string[]) => {
      const values = rawValues.map((s) => (transform ? transform(s) : s)).filter(Boolean);
      if (values.length === 0) return;
      if (validate) {
        const invalid = values.find((v) => validate(v) !== null);
        if (invalid) {
          setError(validate(invalid) || 'Invalid value');
          return;
        }
      }
      setError('');
      const uniqueNew = values.filter((v) => !currentParams.includes(v));
      if (uniqueNew.length > 0) {
        const newParams = [...currentParams, ...uniqueNew];
        setValue(`${fieldPrefix}.${conditionIndex}.paramsRaw`, serializeParamsRaw(newParams));
      }
    },
    [fieldPrefix, currentParams, conditionIndex, setValue, validate, transform],
  );

  const commitInput = useCallback(() => {
    if (!inputRef.current) return;
    const rawValue = inputRef.current.value.trim();
    if (!rawValue) return;
    commitValues([rawValue]);
    inputRef.current.value = '';
    setInputWidth(60);
  }, [commitValues]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        commitInput();
      }
    },
    [commitInput],
  );

  const handleBlur = useCallback(() => {
    commitInput();
  }, [commitInput]);

  // Multi-line paste (e.g. a spreadsheet column, or a list of model numbers) becomes one
  // param per line. Read the raw clipboard text before the browser normalizes it into the
  // input's value — a native single-line <input> otherwise collapses/strips newlines from
  // e.target.value. A single-line paste is left to default browser behavior so it merges
  // into the input like normal typing (still requires Enter/blur to commit).
  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>) => {
      const text = e.clipboardData.getData('text/plain');
      if (!/\r?\n/.test(text)) return;
      e.preventDefault();
      const lines = text
        .split(/\r?\n/)
        .map((s) => s.trim())
        .filter(Boolean);
      commitValues(lines);
      if (inputRef.current) {
        inputRef.current.value = '';
      }
      setInputWidth(60);
    },
    [commitValues],
  );

  return (
    <span className="inline-flex flex-col">
      <input
        ref={inputRef}
        type="text"
        className={`py-0.5 rounded-full border-2 bg-chip-canvas-default-idle px-4 text-sm leading-tight outline-none ${error ? 'border-feedback-error' : 'focus:border-interactive-primary border-chip-border-default-idle'}`}
        style={inputStyle}
        placeholder={placeholder}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        onPaste={handlePaste}
        onInput={resizeInput}
      />
      {error && <span className="text-feedback-error mt-0.5 text-xs">{error}</span>}
    </span>
  );
};

/* ─── Dropdown param adder (finance/program/battery types) ─── */

interface DropdownParamAdderProps {
  fieldPrefix: `conditions` | `inputs.${number}.conditions`;
  currentParams: string[];
  conditionIndex: number;
  setValue: UseFormSetValue<DialogFormValues>;
  options: Array<{ value: string; label: string }>;
  placeholder?: string;
  exclusiveValue?: string;
}

const DropdownParamAdder = ({
  fieldPrefix,
  currentParams,
  conditionIndex,
  setValue,
  options,
  placeholder = '+ add',
  exclusiveValue,
}: DropdownParamAdderProps) => {
  const availableOptions = useMemo(
    () => options.filter((opt) => !currentParams.includes(opt.value)),
    [options, currentParams],
  );

  const handleChange = useCallback(
    (newValue: string) => {
      if (!newValue || currentParams.includes(newValue)) return;
      const newParams = newValue === exclusiveValue ? [newValue] : [...currentParams, newValue];
      setValue(`${fieldPrefix}.${conditionIndex}.paramsRaw`, serializeParamsRaw(newParams));
    },
    [fieldPrefix, currentParams, conditionIndex, setValue, exclusiveValue],
  );

  if (availableOptions.length === 0) {
    return null;
  }

  return <ChipSelect options={availableOptions} value="" onChange={handleChange} placeholder={placeholder} />;
};

/* ─── Option builders for dropdown conditions ─── */

const getFinanceTypeOptions = (): Array<{ value: string; label: string }> =>
  Object.entries(FinanceTypeLabels).map(([value, label]) => ({ value, label }));

const getProgramTypeOptions = (programTypes: string[]): Array<{ value: string; label: string }> =>
  Object.entries(ProgramTypeLabels)
    .filter(([value]) => programTypes.length === 0 || programTypes.includes(value))
    .map(([value, label]) => ({ value, label }));

/* ─── Validators ─── */

const validateStateCode = (value: string): string | null => {
  if (!/^[A-Z]{2}$/.test(value)) return `"${value}" is not a valid 2-letter state code`;
  return null;
};

const transformUpperCase = (value: string): string => value.toUpperCase();

/* ─── Main ConditionChip ─── */

export interface ConditionChipProps {
  fieldPrefix: `conditions` | `inputs.${number}.conditions`;
  conditionIndex: number;
  control: Control<DialogFormValues>;
  getValues: UseFormGetValues<DialogFormValues>;
  setValue: UseFormSetValue<DialogFormValues>;
  removeCondition: (index: number) => void;
  programTypes: string[];
}

export const ConditionChip = ({
  fieldPrefix,
  conditionIndex,
  control,
  getValues,
  setValue,
  removeCondition,
  programTypes,
}: ConditionChipProps) => {
  const utilityLabelMapRef = useRef<Record<string, string>>({});

  const handleUtilityLabelAdd = useCallback((value: string, label: string) => {
    utilityLabelMapRef.current = { ...utilityLabelMapRef.current, [value]: label };
  }, []);

  const conditionName = useWatch({ control, name: `${fieldPrefix}.${conditionIndex}.name` });
  const operator =
    useWatch({ control, name: `${fieldPrefix}.${conditionIndex}.operator` }) || ChecklistConditionOperator.Is;
  const paramsRaw = useWatch({ control, name: `${fieldPrefix}.${conditionIndex}.paramsRaw` }) || '';

  const isNotOperator = operator === ChecklistConditionOperator.IsNot;
  const isOperatorless =
    Boolean(conditionName) && OPERATORLESS_CONDITIONS.has(conditionName as ChecklistConditionName);

  const handleOperatorChange = useCallback(
    (newOperator: string) => {
      setValue(`${fieldPrefix}.${conditionIndex}.operator`, newOperator as ChecklistConditionOperator);
    },
    [fieldPrefix, conditionIndex, setValue],
  );

  const isUtilityCondition = conditionName === ChecklistConditionName.utility;
  const { data: utilitiesData } = useGetUtilitiesQuery({}, { skip: !isUtilityCondition });
  const utilityNameMap = useMemo(() => {
    if (!utilitiesData) return {};
    return Object.fromEntries(
      utilitiesData.map((u: { lseId: number; name: string }) => [u.lseId.toString(), u.name]),
    );
  }, [utilitiesData]);

  const currentParams = useMemo(() => parseParamsRaw(paramsRaw), [paramsRaw]);

  const conditions = getValues(fieldPrefix as 'conditions');
  const nameOptions = useMemo(
    () =>
      Object.entries(ChecklistConditionNameLabels)
        .filter(([value]) => {
          if (value === conditionName) return true;

          return !conditions.some((c, idx) => idx !== conditionIndex && c.name === value);
        })
        .toSorted(([_, labelA], [__, labelB]) => labelA.localeCompare(labelB))
        .map(([value, label]) => ({ value, label })),
    [conditionName, conditions, conditionIndex],
  );

  const isParameterized = conditionName && ParameterizedConditions.has(conditionName as ChecklistConditionName);

  const handleRemove = useCallback(() => {
    removeCondition(conditionIndex);
  }, [removeCondition, conditionIndex]);

  const handleRemoveParam = useCallback(
    (rawValue: string) => {
      const newParams = currentParams.filter((p: string) => p !== rawValue);
      setValue(`${fieldPrefix}.${conditionIndex}.paramsRaw`, serializeParamsRaw(newParams));
    },
    [fieldPrefix, currentParams, conditionIndex, setValue],
  );

  const handleConditionNameChange = useCallback(
    (newName: string) => {
      setValue(`${fieldPrefix}.${conditionIndex}.name`, newName);
      if (!ParameterizedConditions.has(newName as ChecklistConditionName) || newName !== conditionName) {
        setValue(`${fieldPrefix}.${conditionIndex}.paramsRaw`, '');
      }
    },
    [fieldPrefix, conditionIndex, conditionName, setValue],
  );

  const equipmentType = conditionEquipmentType[conditionName as ChecklistConditionName];

  const renderParamAdder = useCallback(() => {
    const name = conditionName as ChecklistConditionName;

    if (name === ChecklistConditionName.state) {
      return (
        <TextParamAdder
          fieldPrefix={fieldPrefix}
          currentParams={currentParams}
          conditionIndex={conditionIndex}
          setValue={setValue}
          placeholder="+ state"
          validate={validateStateCode}
          transform={transformUpperCase}
        />
      );
    }

    if (name === ChecklistConditionName.financeType) {
      return (
        <DropdownParamAdder
          fieldPrefix={fieldPrefix}
          currentParams={currentParams}
          conditionIndex={conditionIndex}
          setValue={setValue}
          options={getFinanceTypeOptions()}
        />
      );
    }

    if (name === ChecklistConditionName.programType) {
      return (
        <DropdownParamAdder
          fieldPrefix={fieldPrefix}
          currentParams={currentParams}
          conditionIndex={conditionIndex}
          setValue={setValue}
          options={getProgramTypeOptions(programTypes)}
        />
      );
    }

    if (name === ChecklistConditionName.battery) {
      if (currentParams.includes('any')) return null;
      return (
        <DropdownParamAdder
          fieldPrefix={fieldPrefix}
          currentParams={currentParams}
          conditionIndex={conditionIndex}
          setValue={setValue}
          options={batteryTypeOptions}
          placeholder="+ battery type"
          exclusiveValue="any"
        />
      );
    }

    if (equipmentType) {
      return (
        <AVLManufacturerParamAdder
          fieldPrefix={fieldPrefix}
          equipmentType={equipmentType}
          currentParams={currentParams}
          conditionIndex={conditionIndex}
          setValue={setValue}
        />
      );
    }

    if (name === ChecklistConditionName.licensedOrganization) {
      return (
        <OrganizationParamAdder
          fieldPrefix={fieldPrefix}
          currentParams={currentParams}
          conditionIndex={conditionIndex}
          setValue={setValue}
        />
      );
    }

    if (name === ChecklistConditionName.utility) {
      return (
        <UtilityParamAdder
          fieldPrefix={fieldPrefix}
          currentParams={currentParams}
          conditionIndex={conditionIndex}
          setValue={setValue}
          onLabelAdd={handleUtilityLabelAdd}
        />
      );
    }

    if (name === ChecklistConditionName.designPlatform) {
      return (
        <DesignPlatformParamAdder
          fieldPrefix={fieldPrefix}
          currentParams={currentParams}
          conditionIndex={conditionIndex}
          setValue={setValue}
        />
      );
    }

    return (
      <TextParamAdder
        fieldPrefix={fieldPrefix}
        currentParams={currentParams}
        conditionIndex={conditionIndex}
        setValue={setValue}
      />
    );
  }, [
    fieldPrefix,
    conditionName,
    currentParams,
    conditionIndex,
    setValue,
    programTypes,
    equipmentType,
    handleUtilityLabelAdd,
  ]);

  return (
    <span className="inline-flex flex-wrap items-center gap-12">
      <ChipSelect
        searchable
        options={nameOptions}
        value={conditionName}
        onChange={handleConditionNameChange}
        placeholder="Select condition"
      />
      {conditionName && !isOperatorless && (
        <ChipSelect options={operatorOptions} value={operator} onChange={handleOperatorChange} variant="grey" />
      )}
      {isParameterized && (
        <>
          {currentParams.map((param: string, idx: number) => (
            <span key={param} className="contents">
              {idx > 0 && <span className="mx-2 text-xs text-text-tertiary">or</span>}
              <ParamTag
                param={param}
                className={isNotOperator ? 'line-through' : undefined}
                displayLabel={
                  isUtilityCondition
                    ? utilityNameMap[param] || utilityLabelMapRef.current[param] || param
                    : getParamLabel(conditionName, param)
                }
                onRemove={handleRemoveParam}
              />
            </span>
          ))}
          {renderParamAdder()}
        </>
      )}
      <button
        type="button"
        onClick={handleRemove}
        className="ml-1.5 cursor-pointer border-none bg-transparent p-0 leading-none text-text-tertiary hover:text-text-primary"
        aria-label="Remove condition"
      >
        <PalmettoIcon name="x" size="xs" />
      </button>
    </span>
  );
};

/* ─── Helpers ─── */

const getParamLabel = (conditionName: string, param: string): string => {
  if (conditionName === ChecklistConditionName.financeType) {
    return FinanceTypeLabels[param as keyof typeof FinanceTypeLabels] || param;
  }
  if (conditionName === ChecklistConditionName.programType) {
    return ProgramTypeLabels[param as keyof typeof ProgramTypeLabels] || param;
  }
  if (conditionName === ChecklistConditionName.battery) {
    if (param === 'any') return 'Any';
    return BatteryTypeLabels[param as keyof typeof BatteryTypeLabels] || param;
  }
  return param;
};
